import catalogData from './rule-config-catalog.data.json';
import { displayValue, readPath, writePath } from './rule-config.util';
import type { CatalogField, CatalogSection, ConfigSection, FieldControl } from './rule-config.types';

export type { ConfigField, ConfigSection, FieldControl } from './rule-config.types';
export { displayValue, readPath, writePath };

type RawFieldTuple = [path: string, label: string, kind: string, ...options: string[]];

interface RawFieldObject {
  path: string;
  label: string;
  kind: string;
  min?: number;
  max?: number;
  step?: number;
  hint?: string;
  options?: string[];
}

type RawField = RawFieldTuple | RawFieldObject;

interface RawCatalogSection {
  key: string;
  title: string;
  fields: RawField[];
}

function parseFieldControl(kind: string, options: string[]): FieldControl {
  if (kind === 'text') return { type: 'text' };
  if (kind === 'number') return { type: 'number' };
  if (kind === 'boolean') return { type: 'boolean' };
  if (kind === 'enum') return { type: 'enum', options };
  return { type: 'text' };
}

function parseField(raw: RawField): CatalogField {
  if (Array.isArray(raw)) {
    const [path, label, kind, ...options] = raw;
    return { path, label, control: parseFieldControl(kind, options) };
  }
  let control = parseFieldControl(raw.kind, raw.options ?? []);
  if (control.type === 'number') {
    control = {
      type: 'number',
      ...(raw.min != null && { min: raw.min }),
      ...(raw.max != null && { max: raw.max }),
      ...(raw.step != null && { step: raw.step }),
    };
  }
  return { path: raw.path, label: raw.label, ...(raw.hint != null && { hint: raw.hint }), control };
}

function parseCatalog(sections: RawCatalogSection[]): CatalogSection[] {
  return sections.map((section) => ({
    key: section.key,
    title: section.title,
    fields: section.fields.map(parseField),
  }));
}

const RULE_CONFIG_CATALOG = parseCatalog(
  (catalogData as { sections: RawCatalogSection[] }).sections,
);

export const RULE_CONFIG_SECTIONS: ConfigSection[] = RULE_CONFIG_CATALOG.map((section) => ({
  key: section.key,
  title: section.title,
  fields: section.fields.map(({ path, label, hint }) => ({ path, label, ...(hint != null && { hint }) })),
}));

export const RULE_CONFIG_FIELD_TYPES: Record<string, FieldControl> = Object.fromEntries(
  RULE_CONFIG_CATALOG.flatMap((section) => section.fields.map((field) => [field.path, field.control])),
);

export function fieldControl(path: string): FieldControl {
  return RULE_CONFIG_FIELD_TYPES[path] ?? { type: 'text' };
}
